import { ApolloProvider, ApolloQueryResult } from "@apollo/client";
import CssBaseline from "@material-ui/core/CssBaseline";
import { MuiThemeProvider } from "@material-ui/core/styles";
import React, { useReducer } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import track from "react-tracking";

import { AnalyticsObj, trackEvent, trackPageView } from "analytics/index";
import reducer from "appReducer";
import { Loading, NotesModal, Notifier, ServerError } from "components/simple";
import client from "graphql/client";
import Routes from "routes/routes";
import theme from "style/theme";
import { Roles } from "utils/auth";

import "style/App.css";

export const DispatchContext = React.createContext<{
  state: Props["initialState"];
  dispatch: any;
}>({
  state: null as any,
  dispatch: null as any
});

interface Props {
  initialState: {
    notifier: {
      isOpen: boolean;
      status: string;
      title: string;
      description: string;
    };
    loading: boolean;
    error: boolean;
    notesModal: {
      openModal: boolean;
      noteId?: string;
      noteText?: string;
      userAssessmentCycleId?: string;
      userAssessmentId?: string;
      refetch?: () => Promise<ApolloQueryResult<any>>;
    };
    role?: Roles;
  };
}

const App: React.FC<Props> = ({ initialState }: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  return (
    <ApolloProvider client={client}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <DispatchContext.Provider value={{ state, dispatch }}>
          <Notifier
            description={state.notifier.description}
            open={state.notifier.isOpen}
            close={() => {
              dispatch({ type: "close" });
            }}
            status={state.notifier.status}
            title={state.notifier.title}
          />
          {state.notesModal.openModal && (
            <NotesModal
              userAssessmentCycleId={state.notesModal.userAssessmentCycleId}
              userAssessmentId={state.notesModal.userAssessmentId}
              noteId={state.notesModal.noteId}
              noteText={state.notesModal.noteText}
              refetch={state.notesModal.refetch}
            />
          )}
          <Router>
            {state.loading && <Loading />}
            {state.error && <ServerError />}
            <Routes />
          </Router>
        </DispatchContext.Provider>
      </MuiThemeProvider>
    </ApolloProvider>
  );
};

export default track(
  {},
  {
    dispatch: (trackingData: AnalyticsObj) => {
      if (process.env.REACT_APP_CUSTOM_NODE_ENV !== "production") return;
      const matomo: any = window._paq; // eslint-disable-line
      if (!matomo || !trackingData) return;
      if (trackingData.actions) trackEvent(matomo, trackingData);
    },
    process: (trackingData: AnalyticsObj) => {
      if (process.env.REACT_APP_CUSTOM_NODE_ENV !== "production") return;
      const matomo: any = window._paq; // eslint-disable-line
      if (!matomo || !trackingData) return;
      if (trackingData.url) trackPageView(matomo, trackingData);
      return false;
    }
  }
)(App);
